import type { ContentTranslations } from './translation';

/**
 * Role ladder (HIERARCHY_SPEC §2): super_admin → district_admin →
 * school_admin → coach / team_manager → athlete / parent. Every organization
 * is a school — inside a district or standalone — so its admin is a
 * school_admin either way. The role string itself carries no rule authority:
 * claims sync mints the `admin` / `org_admin` claims from
 * `organizations.admin_ids`, and `district_admin` from `districts.admin_ids`.
 */
export type UserRole = 'athlete' | 'coach' | 'school_admin' | 'super_admin' | 'player' | 'parent' | 'student' | 'team_manager' | 'district_admin';

/**
 * How an account came to exist. 'managed' accounts are owned by a district
 * (SSO + roster sync): their name/email/role come from the SIS and are not
 * user-editable. Everything predating districts is 'self_service'.
 */
export type AccountType = 'managed' | 'self_service';

export type ProvisionedBy = 'roster_sync' | 'sso_jit' | 'self' | 'admin_invite';

export type DeactivationReason =
  | 'left_roster'
  | 'district_suspended'
  | 'admin_action';

export type OrganizationType = 'organization' | 'group';

// ============================================================================
// PRIVACY SYSTEM TYPES
// ============================================================================

// V1 Simplified Privacy: Profile visibility controls content visibility to org members
// 'public' = full profile content visible to org/team members
// 'private' = only name, photo, role, sport visible to org/team members
export type ProfileVisibility = 'public' | 'private';

export type ReactionEmoji = '👍' | '❤️' | '👏' | '🔥' | '🎉';
export const REACTION_EMOJIS: ReactionEmoji[] = ['👍', '❤️', '👏', '🔥', '🎉'];

export type AnnouncementTargetAudience = 'all' | 'org_admins' | 'coaches' | 'athletes' | 'parents';

/**
 * Single source of truth for the sellable surface. Server-side validation
 * (districts.ts) and the entitlement resolver both derive from this list —
 * same pattern as ROSTERING_SOURCES, which drifted from its validator once
 * and should not again.
 */
export const ORGANIZATION_PERMISSIONS = [
  'ai_analysis',
  'advanced_analytics',
  'team_management',
  'challenge_creation',
  'video_storage',
  'custom_branding',
] as const;

export type OrganizationPermission = (typeof ORGANIZATION_PERMISSIONS)[number];

export type InvitationStatus = 'pending' | 'accepted' | 'pending_approval' | 'approved' | 'rejected' | 'expired';

export type ParentLinkStatus = 'pending' | 'active' | 'revoked';
export type LinkageState = 'unlinked' | 'pending' | 'linked' | 'suspended';

/**
 * Represents a parent-child relationship link
 * Stored at /clients/{clientKey}/parent_links/{linkId}
 */
export interface ParentLink {
  id: string;
  parent_id: string;           // Parent's user ID
  child_id: string;            // Athlete's user ID
  status: ParentLinkStatus;
  invite_code: string;         // Code used to establish the link
  // Denormalized data for easier querying
  parent_email?: string;
  parent_name?: string;
  child_name?: string;
  child_team_ids?: string[];   // Teams the child is on (for parent document uploads)
  // Timestamps
  created_at: Date;
  linked_at?: Date;            // When status changed to 'active'
  revoked_at?: Date;           // When status changed to 'revoked'
  revoked_by?: string;         // Who revoked the link (parent_id, child_id, or admin)
  // Notification preferences for this link
  notification_preferences?: {
    session_completed: boolean;
    challenge_completed: boolean;
    coach_messages: boolean;
    team_announcements: boolean;
  };
  // How the link came to exist: invite codes (self-service families) or the
  // district roster's guardian edges (no codes; server-created)
  source?: 'invite_code' | 'roster';
  district_id?: string;
}

/**
 * The public schedule page (AUTOMATION_SPEC A10). Server-owned: written only
 * by `setPublicSchedule`, and rules deny the key to clients. The slug is
 * minted on the first enable and kept when the page is switched off, so
 * re-enabling gives back the link the school already printed.
 */
export interface PublicScheduleState {
  enabled: boolean;
  slug: string;
  url: string;
  updated_at?: Date | { toDate: () => Date } | null;
  updated_by?: string;
}

export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  description?: string;
  logo_url?: string;
  admin_ids: string[]; // Array of organization admin user IDs
  /**
   * Set/cleared only by upsertDistrict when this org is a school in a
   * district (HIERARCHY_SPEC §3); client writes are blocked in rules.
   */
  district_id?: string | null;
  created_at: Date;
  updated_at: Date;
  is_active: boolean;
  // Organization-specific fields
  billing: {
    /**
     * 'none' = this org does not pay for itself; it is either covered by its
     * district (BILLING_SPEC §5.3) or unlicensed. The two are distinguished by
     * resolution, not by a second field.
     */
    plan_type: 'none' | 'basic' | 'premium';
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    billing_email?: string;
    next_billing_date?: Date;
    status: 'pending' | 'active' | 'cancelled' | 'canceled' | 'past_due' | 'trialing'; // pending = hasn't paid yet, cancelled/canceled for compatibility
    // Additional billing fields
    payment_type?: 'subscription' | 'one_time' | 'invoice'; // How they pay
    billing_cycle?: 'monthly' | 'yearly'; // Billing frequency
    price_cents?: number; // Price in cents
  };
  permissions: OrganizationPermission[]; // Permissions enabled for this org
  settings: {
    allow_public_teams: boolean;
    require_admin_approval: boolean;
    max_members?: number;
    custom_domain?: string;
  };
  invite_code?: string; // For easy joining
  metadata?: {
    industry?: string;
    location?: string;
    website?: string;
    contact_email?: string;
  };
  // Custom branding (premium feature)
  branding?: {
    primary_color?: string; // Hex color code (e.g., '#00F0FF')
    secondary_color?: string; // Hex color code (e.g., '#FF0080')
    logo_url?: string; // Organization logo URL
    logo_position?: { x: number; y: number }; // Logo position offset
    logo_zoom?: number; // Logo zoom level (1 = 100%)
  };
  // Athleto representative assigned to this organization
  athleto_rep_id?: string; // User ID of the assigned Athleto rep
  // Message monitoring settings
  monitoring?: {
    lastAnalyzedAt?: Date; // Last time messages were analyzed by GPT
  };
  /** Public schedule page (AUTOMATION_SPEC A10); written only by setPublicSchedule. */
  public_schedule?: PublicScheduleState;
  /**
   * The "human API" (AUTOMATION_SPEC §5 T1): the people a bus request or a
   * facility hold goes to, and the organization's timezone. Mirrors
   * `common/src/types/user.ts`.
   */
  operations?: {
    transportation?: { name?: string; email: string };
    facilities?: { name?: string; email: string };
    timezone?: string;
  };
}

export interface OrganizationMembership {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'member' | 'admin'; // Role within the organization
  joined_at: Date;
  permissions: OrganizationPermission[]; // User's enabled permissions within org
  status: 'active' | 'suspended' | 'pending_approval';
  invited_by?: string;
  approved_by?: string;
  approved_at?: Date;
}

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  invited_user_id?: string; // If inviting specific user
  invited_email?: string; // If inviting by email
  invited_by: string;
  invite_code: string;
  status: InvitationStatus;
  expires_at: Date;
  created_at: Date;
  accepted_at?: Date; // When user accepted the invitation
  approved_by?: string;
  approved_at?: Date;
  rejected_by?: string;
  rejected_at?: Date;
  rejection_reason?: string;
}

export interface RecruiterVerificationRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_display_name: string;
  university: string;
  position: string;
  recruiting_regions: string[];
  sports_focus: string[];
  university_email: string; // Must be from university domain
  phone_number?: string;
  additional_info?: string;
  proof_documents?: string[]; // URLs to uploaded documents (ID, employment verification, etc.)
  status: 'pending' | 'approved' | 'rejected';
  created_at: Date;
  updated_at: Date;
  reviewed_by?: string; // Super admin who reviewed
  reviewed_at?: Date;
  rejection_reason?: string;
}

export interface Achievement {
  id: string;
  title: string;
  description?: string;
  type: 'championship' | 'tournament' | 'award' | 'milestone' | 'other';
  level?: 'state' | 'regional' | 'national' | 'international';
  date?: Date;
  organization?: string;
  image_url?: string;
}

export interface UserProfile {
  uid: string;
  email: string | null;
  display_name: string | null;
  photo_url: string | null;
  photo_position?: { x: number; y: number }; // Position for profile photo cropping
  photo_zoom?: number; // Zoom level for profile photo (1.0 = 100%, 2.0 = 200%, etc.)
  cover_photo_url?: string | null;
  cover_photo_position?: { x: number; y: number }; // Position for cover photo cropping
  cover_photo_zoom?: number; // Zoom level for cover photo (1.0 = 100%, 2.0 = 200%, etc.)
  role: UserRole;
  created_at: Date;
  updated_at: Date;
  // Organization membership
  organization_id?: string; // Primary organization
  organization_memberships?: string[]; // All organization memberships
  enabled_permissions?: OrganizationPermission[]; // Permissions enabled by org admin
  // Team memberships
  team_ids?: string[]; // All teams the user is part of
  // Access linkage states
  org_link_state?: LinkageState;
  team_link_state?: LinkageState;
  access_state_updated_at?: Date;
  access_state_reason?: string;
  pending_org_request_id?: string;
  pending_team_request_id?: string;
  pending_org_id?: string;
  pending_team_id?: string;
  pending_scope_updated_at?: Date;
  metadata?: {
    sport?: string; // Deprecated - use sports instead
    sports?: string[]; // Multiple sports support
    team?: string;
    location?: string;
    bio?: string;
    featured_coins?: string[]; // Array of coin IDs to display on profile
    // Athlete-specific fields
    club_teams?: string[]; // Names of club teams
    graduation_year?: number;
    height?: string;
    weight?: string;
    position?: string;
    // Coach-specific fields
    coaching_experience?: string;
    specialization?: string;
    certifications?: string[];
    // Recruiter-specific fields
    university?: string;
    recruiting_regions?: string[];
    sports_focus?: string[];
  };
  // Achievements (primarily for athletes)
  achievements?: Achievement[];
  // Recruiter verification
  is_verified_recruiter?: boolean;
  verified_at?: Date;
  verification_badge?: {
    verified: boolean;
    verified_by?: string; // Super admin who verified
    verified_at?: Date;
    university?: string;
    position?: string;
  };
  // Onboarding
  has_completed_onboarding?: boolean;
  // Push notification tokens
  fcm_tokens?: string[]; // Firebase Cloud Messaging tokens for push notifications
  notification_settings?: {
    enabled: boolean;
    message_notifications: boolean;
    mention_notifications: boolean;
    channel_notifications: boolean;
    announcement_notifications?: boolean;
    email_digests?: boolean; // Daily email digest of unread announcements
  };
  // Content translation (docs/TRANSLATION_SPEC.md §4.2)
  preferred_language?: string; // BCP-47 tag, e.g. 'es', 'zh', 'pt-BR'. Unset = 'en'
  auto_translate?: boolean;    // Render foreign-language content translated by default
  // Admin mirroring support
  is_mirroring?: boolean;
  currently_mirroring_uid?: string;
  currently_mirroring_name?: string;
  // Parent-child linking
  linked_parent_id?: string;           // For athletes: first linked parent (legacy single slot; kept one release, see linked_parent_ids)
  linked_parent_ids?: string[];        // For athletes: every linked parent/guardian (ROSTERING_COMPLETION_SPEC G8)
  linked_children_ids?: string[];      // For parents: array of linked children's user IDs
  parent_invite_code?: string;         // For athletes: code to share with parent
  parent_invite_code_expires_at?: Date; // Expiration for the invite code

  // ============================================================================
  // DISTRICT IDENTITY & ROSTERING (SSO_SPEC §5.4)
  // Server-owned: written by roster sync / SSO blocking functions, never the client
  // ============================================================================
  account_type?: AccountType;          // Absent is treated as 'self_service'
  district_id?: string;
  sis_sourced_id?: string;             // Links back to roster_users
  school_org_ids?: string[];           // Schools from the roster
  managed_fields_locked?: boolean;     // UI hint: name/email/role not editable
  provisioned_by?: ProvisionedBy;
  deactivated?: boolean;               // Set by sync when a user leaves the roster
  deactivated_reason?: DeactivationReason;

  // ============================================================================
  // PRIVACY SETTINGS (V1 Simplified)
  // ============================================================================
  profile_visibility: ProfileVisibility; // 'public' | 'private' - controls content visibility to org members
}

export interface Session {
  id: string;
  user_id: string;
  sport: string;
  timestamp: Date;
  duration: number; // in seconds
  video_url?: string;
  thumbnail_url?: string;
  metrics: Record<string, any>;
  is_public: boolean;
  notes?: string;
  likes?: number;
  comment_count?: number;
}

export interface TeamPost {
  id: string;
  team_id: string;
  user_id: string;
  user_display_name?: string;
  user_photo_url?: string;
  // Aliases for compatibility with different naming conventions
  author_name?: string; // Alias for user_display_name
  author_photo?: string; // Alias for user_photo_url
  content: string;
  type: 'post' | 'challenge_completion' | 'announcement';
  challenge_id?: string; // For challenge completion posts
  challenge_name?: string;
  challenge_value?: number;
  challenge_unit?: string;
  challenge_target_value?: number;
  media_url?: string;
  video_url?: string;
  created_at: Date;
  likes: string[]; // User IDs who liked (legacy)
  comments: Comment[]; // Legacy - comments removed in favor of reactions
  // Reaction system (replaces likes)
  reaction_counts?: ReactionCounts;
  reaction_count_total?: number;
  // Announcement/feed extensions
  title?: string; // Optional headline (announcements)
  attachments?: PostAttachment[]; // Downloadable files
  is_pinned?: boolean; // Pinned to top of feed (coach/manager only)
  comment_count?: number;
  updated_at?: Date;
  // Read receipts (announcements)
  read_by?: string[];
  read_count?: number;
  // Denormalized team roles so security rules can allow coach/manager moderation
  team_coach_ids?: string[];
  team_manager_ids?: string[];
  // Machine translation — server-written only, forgery-guarded in rules
  // (docs/TRANSLATION_SPEC.md §4.5, §8)
  detected_lang?: string;
  translations?: ContentTranslations;
}

export interface PostAttachment {
  id: string;
  name: string; // Original filename shown to users
  url: string; // Download URL
  content_type: string;
  size: number; // Bytes
  storage_path: string; // Full Storage path for cleanup on delete
}

export interface TeamPostComment {
  id: string;
  post_id: string;
  team_id: string;
  user_id: string;
  user_display_name?: string;
  user_photo_url?: string;
  content: string;
  created_at: Date;
  // Denormalized team roles so security rules can allow coach/manager moderation
  team_coach_ids?: string[];
  team_manager_ids?: string[];
}

export interface Team {
  id: string;
  name: string;
  sport: string;
  description?: string;
  coach_id: string; // Legacy field - keeping for backward compatibility
  coach_ids: string[]; // Array of coach user IDs (supports multiple coaches)
  manager_ids?: string[]; // Array of team manager user IDs (can manage team but not full coach privileges)
  athlete_ids?: string[]; // Array of athlete user IDs
  members: string[]; // User IDs
  created_at: Date;
  updated_at: Date;
  invite_code?: string; // Code for athletes to join the team
  org_link_code?: string; // Code for linking team to an organization
  is_private: boolean;
  photo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  cover_photo_url?: string | null; // Team cover photo
  cover_photo_position?: { x: number; y: number }; // Position for cover photo cropping
  cover_photo_zoom?: number; // Zoom level for cover photo (1.0 = 100%, 2.0 = 200%, etc.)
  cover_photo_bg_color?: string; // Background color when image doesn't fill container (hex code)
  challenges?: string[]; // Challenge IDs
  location?: string;
  // Organization integration
  organization_id?: string; // If team belongs to an organization
  scope: 'public' | 'organization' | 'group'; // Team visibility scope
  /**
   * Set when the roster comes from a district team roster file
   * (TEAM_SOURCING_SPEC). Server-owned: rules deny the key to clients.
   */
  sourced_from?: {
    district_id: string;
    key: string;
    season?: string | null;
    last_synced_at?: Date | { toDate: () => Date } | null;
    last_count?: number;
  } | null;
  /** Public schedule page (AUTOMATION_SPEC A10); written only by setPublicSchedule. */
  public_schedule?: PublicScheduleState;
  /** Subscribed district schedule feed (AUTOMATION_SPEC §3.4); written only by setScheduleFeed. */
  schedule_feed?: {
    url: string;
    enabled: boolean;
    timezone?: string;
    last_synced_at?: Date | { toDate: () => Date } | null;
    last_error?: string | null;
    last_count?: number;
  };
  settings?: {
    rostersEnabled?: boolean;
    playbookEnabled?: boolean;
    documentsEnabled?: boolean;
    trainingLibraryEnabled?: boolean;
    lineupsEnabled?: boolean; // All sports - starting lineups & depth charts
    baserunningEnabled?: boolean; // Baseball/softball only
    defensiveAlignmentsEnabled?: boolean; // Baseball/softball only
    // Parent messaging settings
    allowParentCoachMessaging?: boolean; // If true, parents can initiate DMs with coaches
  };
  // Roster system
  rosters?: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  // Squads, jersey numbers and positions live on roster_entries (ROSTER_MODEL_SPEC
  // R1; the team-doc maps were retired in ROSTERING_COMPLETION_SPEC G7).
  // Team positions (coach-customizable)
  positions?: TeamPosition[]; // Custom positions for this team (falls back to sport defaults if not set)
  // Parent tracking
  parent_ids?: string[];  // Array of parent user IDs linked to athletes on this team
  // Golf-specific: Strategy layer definitions (shared across all courses for this team)
  strategyLayers?: Array<{
    id: string;
    name: string;
    color: string;
    order: number;
  }>;
  stats?: {
    total_sessions: number;
    average_score: number;
    active_challenges: number;
  };

  // ============================================================================
  // SPORT-TYPE SPECIFIC FIELDS
  // ============================================================================

  // Formation-based sports (Football, Soccer) - custom formations created by coaches
  customFormations?: {
    offense: FormationTemplate[];
    defense: FormationTemplate[];
  };

  // Ranking-based sports (Tennis, Golf) - athlete rankings
  rankings?: {
    [slotId: string]: {
      athletes: RankedAthlete[];
      updatedAt: Date;
    };
  };

  // Tennis doubles - pair 2 players per slot
  doublesParings?: {
    [slotId: string]: [string, string]; // Pair of athlete IDs
  };

  // Weight class sports (Wrestling) - athletes assigned to weight classes
  weightClassAssignments?: {
    [weightClassId: string]: {
      athletes: RankedAthlete[];
      updatedAt: Date;
    };
  };

  // Event-based sports (Track & Field) - athletes assigned to events
  eventAssignments?: {
    [eventId: string]: string[]; // Array of athlete IDs
  };

  // Golf settings for ladder lineup
  golfSettings?: {
    varsityCount: number; // 4-6 golfers on varsity
  };

  // Tennis settings for ladder lineup
  tennisSettings?: {
    singlesCount: number; // 1-6 singles spots
    doublesCount: number; // 1-3 doubles teams
  };
}

// ============================================================================
// SPORT-TYPE SPECIFIC INTERFACES
// ============================================================================

/**
 * Ranked athlete for ranking-based and weight-class sports
 */
export interface RankedAthlete {
  athleteId: string;
  rank: number; // 1 = starter/top, 2 = backup, etc.
  handicap?: number; // For golf
  notes?: string;
}

/**
 * Formation template for football/soccer
 */
export interface FormationTemplate {
  id: string;
  name: string;
  category: PositionCategory;
  description?: string;
  positions: FormationPosition[];
  isSystemPreset: boolean;
  createdAt?: Date;
  createdBy?: string;
}

/**
 * Position within a formation
 */
export interface FormationPosition {
  id: string;
  label: string;
  abbreviation: string;
  x: number; // 0-100 percentage
  y: number; // 0-100 percentage
}

export interface TeamJoinRequest {
  id: string;
  team_id: string;
  user_id: string;
  user_display_name: string;
  user_photo_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: Date;
  reviewed_at?: Date;
  reviewed_by?: string; // Coach/manager who approved/rejected
}

export interface TeamDocument {
  id: string;
  team_id: string;
  name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_at: Date;
  description?: string;
}

// ============================================================================
// TEAM POSITION TYPES
// ============================================================================

export type PositionCategory = 'offense' | 'defense' | 'both' | 'special';

/**
 * Team-specific position configuration
 * Coaches can customize these per team
 */
export interface TeamPosition {
  id: string;              // Unique identifier (e.g., 'PG', 'QB', 'P')
  label: string;           // Full name (e.g., 'Point Guard', 'Quarterback')
  abbreviation: string;    // Short form (e.g., 'PG', 'QB')
  category: PositionCategory; // For sports with offense/defense split
  order: number;           // Display order
  slotNumber?: number;     // Lineup slot number (1-based, maps to lineup slots)
  x?: number;              // Optional field position for playbook (0-100)
  y?: number;              // Optional field position for playbook (0-100)
}

// ============================================================================
// LINEUP & DEPTH CHART TYPES (All Sports)
// ============================================================================

/**
 * Starting lineup for any sport
 * Stores which players are assigned to which positions
 */
export interface StartingLineup {
  id: string;
  team_id: string;
  roster_id: string; // Which roster this lineup belongs to
  sport: string;
  name: string; // "vs Zone Defense", "Power Lineup", "Game vs Eagles"
  category?: PositionCategory; // For sports with offense/defense, which category this lineup is for
  positions: {
    [positionId: string]: string; // positionId -> playerId (empty string = unassigned)
  };
  position_labels?: {
    [positionId: string]: string; // Custom labels per position (e.g., "G" instead of "PG")
  };
  is_active: boolean; // Current/default lineup
  notes?: string;
  created_by: string;
  created_by_name?: string;
  created_at: Date;
  updated_at: Date;
  // Security fields
  team_coach_ids: string[];
  team_manager_ids?: string[];
}

/**
 * Depth chart showing player depth at each position
 */
export interface DepthChart {
  id: string;
  team_id: string;
  roster_id: string; // Which roster this depth chart belongs to
  sport: string;
  name: string; // "Varsity Depth Chart" or "Fall 2024"
  positions: {
    [positionId: string]: DepthChartPosition;
  };
  is_active: boolean; // Current depth chart for the team
  season?: string; // "Fall 2024"
  notes?: string;
  created_by: string;
  created_at: Date;
  updated_at: Date;
  // Security fields
  team_coach_ids: string[];
  team_manager_ids?: string[];
}

export interface DepthChartPosition {
  position_id: string; // 'P', '1B', 'PG', 'QB', etc.
  position_name: string; // "Pitcher", "First Base", "Point Guard"
  players: DepthChartEntry[]; // Ordered by depth (index 0 = starter)
}

export interface DepthChartEntry {
  player_id: string;
  depth: number; // 1 = starter, 2 = backup, etc.
  notes?: string; // "Throws left", "Utility"
}

// ============================================================================
// BASEBALL/SOFTBALL SPECIFIC LINEUP TYPES
// ============================================================================

/**
 * Game lineup for baseball/softball with batting order and inning tracking
 */
export interface GameLineup {
  id: string;
  team_id: string;
  sport: 'baseball' | 'softball';
  game_date: Date;
  opponent?: string;
  is_home: boolean;
  // Batting order (array index = batting position, 0-8 for 9-player, 0-9 for 10-player DP/FLEX)
  batting_order: LineupSlot[];
  // Defensive positions by inning
  defensive_matrix: {
    [inning: number]: {
      [positionId: string]: string | null; // Player ID or null if empty
    };
  };
  // DP/FLEX tracking for softball
  dp_flex?: {
    dp_player_id: string | null; // Designated Player
    flex_player_id: string | null; // FLEX (plays defense only)
  };
  // Substitution log
  substitutions: Substitution[];
  // Metadata
  status: 'draft' | 'active' | 'completed';
  notes?: string;
  created_by: string;
  created_at: Date;
  updated_at: Date;
  // Security fields
  team_coach_ids: string[];
  team_manager_ids?: string[];
}

export interface LineupSlot {
  player_id: string;
  jersey_number?: string;
  starting_position: string; // 'P', 'C', '1B', etc. or 'DH', 'DP', 'FLEX'
  is_starter: boolean;
}

export interface Substitution {
  id: string;
  inning: number;
  half: 'top' | 'bottom';
  player_out_id: string;
  player_in_id: string;
  position: string;
  reason?: string; // "Pinch hitter", "Defensive replacement"
  timestamp: Date;
}

// ============================================================================
// DEFENSIVE ALIGNMENT TYPES (Baseball/Softball)
// ============================================================================

export type DefensiveAlignmentType =
  | 'normal_depth'
  | 'double_play_depth'
  | 'corners_in'
  | 'infield_in'
  | 'no_doubles'
  | 'wheel_play'
  | 'outfield_shift_left'
  | 'outfield_shift_right'
  | 'slap_defense'
  | 'bunt_defense'
  | 'custom';

export interface DefensiveAlignment {
  id: string;
  name: string;
  description?: string;
  sport: 'baseball' | 'softball';
  positions: {
    [positionId: string]: {
      x: number; // 0-100 percentage
      y: number; // 0-100 percentage
      label: string;
    };
  };
  alignment_type: DefensiveAlignmentType;
  is_default: boolean; // Show in quick-select
  team_id: string;
  created_by: string;
  created_at: Date;
  updated_at: Date;
  // Security fields
  team_coach_ids: string[];
  team_manager_ids?: string[];
}

// ============================================================================
// BASERUNNING GUIDE TYPES (Baseball/Softball)
// ============================================================================

export type BaserunningScenarioType =
  | 'grounder'
  | 'fly_ball'
  | 'steal'
  | 'delayed_steal'
  | 'pickoff'
  | 'hit_and_run'
  | 'first_movement'
  | 'custom';

export interface BaserunningGuide {
  id: string;
  name: string;
  sport: 'baseball' | 'softball';
  scenario_type: BaserunningScenarioType;
  base_situation: string; // "runner_on_first", "runner_on_second", "runners_on_first_and_third", etc.
  steps: BaserunningStep[];
  team_id: string;
  is_template: boolean; // Can be cloned from system templates
  created_by: string;
  created_at: Date;
  updated_at: Date;
  // Security fields
  team_coach_ids: string[];
  team_manager_ids?: string[];
}

export interface BaserunningStep {
  order: number;
  instruction: string;
  condition?: string; // "If ball is hit to left side", "With less than 2 outs"
}

export interface Follow {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: Date;
}

export interface Comment {
  id: string;
  session_id: string;
  user_id: string;
  content: string;
  created_at: Date;
  updated_at: Date;
  likes?: number;
  parent_id?: string; // For nested comments
}

export interface Like {
  id: string;
  user_id: string;
  target_id: string; // Can be session ID or comment ID
  target_type: 'session' | 'comment';
  created_at: Date;
}

export interface Notification {
  id: string;
  user_id: string;
  type: 'like' | 'comment' | 'follow' | 'team_invite' | 'challenge' | 'mention' | 'message' | 'channel_message' | 'announcement' | 'parent_link_request' | 'parent_linked' | 'child_session' | 'child_challenge' | 'eligibility' | 'eligibility_digest' | 'game_day_eligibility' | 'attendance_prompt' | 'film_ready' | 'film_failed' | 'film_moments' | 'schedule_conflict'
    | 'job_stale' | 'department_brief';
  title: string;
  message: string;
  read: boolean;
  created_at: Date;
  related_id?: string; // ID of the related entity (session, user, conversation, etc.)
  related_type?: string;
  // For message notifications
  conversation_id?: string;
  sender_id?: string;
  sender_name?: string;
  sender_photo_url?: string;
  // For parent notifications
  child_id?: string;
  child_name?: string;
  // For announcement notifications
  team_id?: string;
  organization_id?: string;
}

export type CoinColor = 'gold' | 'silver' | 'bronze' | 'black' | 'white';
export type CoinEffect = 'fire' | 'sparkle' | 'glow' | 'none';
export type CoinRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface CoinReward {
  front_image_url: string;
  back_image_url: string;
  name: string;
  description?: string;
  color?: CoinColor;
  effect?: CoinEffect;
  rarity?: CoinRarity;
}

export type ChallengeType = 'skill' | 'fitness' | 'competition' | 'team' | 'individual' | 'other';

export interface Challenge {
  id: string;
  name: string;
  description: string;
  sport: string;
  sports?: string[]; // Optional multi-sport targeting (primarily organization challenges)
  type: ChallengeType; // Type of challenge
  target_value?: number; // Target value for the challenge (e.g., 100 shots made)
  unit?: string; // Unit for the target value (e.g., 'shots', 'yards', 'seconds')
  deadline: Date;
  has_deadline?: boolean; // Optional deadline support (false = ongoing challenge)
  start_date?: Date;
  end_date?: Date;
  created_by: string; // Coach/Admin user ID
  created_by_name?: string; // Denormalized creator name for display
  creator_id?: string; // Alias for created_by
  created_at: Date;
  updated_at: Date;
  is_public: boolean;
  participants: string[]; // User IDs
  completions: ChallengeCompletion[];
  rules?: string;
  max_participants?: number;
  team_id?: string; // For team-specific challenges
  team_name?: string; // Denormalized team name for display
  group_id?: string; // For group-specific challenges
  organization_name?: string; // Denormalized organization name for display
  reward?: string;
  coin_reward?: CoinReward; // 3D coin reward for admin challenges
  // Organization integration
  organization_id?: string; // If challenge belongs to an organization (the creating org)
  scope: 'organization' | 'team' | 'athleto_wide'; // Challenge visibility scope (V1: removed public/group)
  required_permissions?: OrganizationPermission[]; // Permissions needed to participate
}

export interface ChallengeCompletion {
  id: string;
  challenge_id: string;
  user_id: string;
  completed_at: Date;
  value: number;
  notes?: string;
  video_url?: string;
  verified?: boolean;
  session_id?: string; // Link to session if applicable
  coin_earned?: boolean; // Whether the user earned the coin reward
}

export interface UserCoin {
  id: string;
  user_id: string;
  challenge_id: string;
  coin_reward: CoinReward;
  earned_at: Date;
  challenge_name: string;
  // Source tracking - where the coin came from
  source_type?: 'public' | 'group' | 'team' | 'organization';
  source_id?: string; // group_id, team_id, or organization_id
  source_name?: string; // Display name of the source
}

// V1: Groups removed - cross-org social features disabled for school safety

export interface RolePermissions {
  can_access_analysis: boolean;
  can_access_challenges: boolean;
  can_access_feed: boolean;
  can_access_admin: boolean;
  can_access_team_management: boolean;
  can_view_all_profiles: boolean;
  can_manage_team: boolean;
  can_create_challenges: boolean;
  // Organization management permissions
  can_manage_organizations: boolean;
  can_manage_organization_members: boolean;
  can_set_organization_permissions: boolean;
}

export interface UserPermissions {
  // Base role permissions
  base_permissions: RolePermissions;
  // Organization-specific permissions (enabled by org admin)
  organization_permissions: OrganizationPermission[];
  // Computed effective permissions
  effective_permissions: {
    can_access_ai_analysis: boolean;
    can_access_advanced_analytics: boolean;
    can_access_analysis: boolean;
    can_access_challenges: boolean;
    can_access_feed: boolean;
    can_access_admin: boolean;
    can_access_team_management: boolean;
    can_view_all_profiles: boolean;
    can_manage_team: boolean;
    can_create_challenges: boolean;
    can_manage_organizations: boolean;
    can_manage_organization_members: boolean;
    can_set_organization_permissions: boolean;
  };
}

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  athlete: {
    can_access_analysis: false, // Now requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  player: {
    can_access_analysis: false, // Now requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  student: {
    can_access_analysis: false, // Now requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  parent: {
    can_access_analysis: false,
    can_access_challenges: false,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  coach: {
    can_access_analysis: false, // Now requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: true,
    can_view_all_profiles: true,
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  team_manager: {
    can_access_analysis: false, // Now requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: true,
    can_view_all_profiles: true,
    can_manage_team: true,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  // V1: recruiter role removed for school safety
  // School admin: runs one school's program (teams, rosters, announcements,
  // and billing when the school is standalone).
  school_admin: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false, // No platform admin access - school admin only
    can_access_team_management: true,
    can_view_all_profiles: false, // Can only view profiles within their school
    can_manage_team: true, // Can manage teams within their school
    can_create_challenges: true,
    can_manage_organizations: false, // Cannot create/delete orgs (super_admin only)
    // HIERARCHY_SPEC §2: school-scoped power is the server-minted org_admin[]
    // claim, never the role string — `school_admin` is a self-assignable
    // profile label (it is not in the server's PRIVILEGED_ROLES), so granting
    // these here would hand the org-member console to anyone who typed it.
    // composeRolePermissions() layers them back on from the claim.
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  // District IT / athletic director: administers every school in their
  // district, but has no platform-wide access. Granted only by roster sync or
  // a super admin — never self-selected at onboarding.
  district_admin: {
    can_access_analysis: false,
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: true,
    can_view_all_profiles: false, // Scoped to their district's schools
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: false, // Schools are created by roster sync
    // DISTRICT_ROLE_SPEC §2: sees everything district-scoped, manages nothing
    // school-scoped. School admin power is the server-minted org_admin[] claim,
    // which a district admin may also hold — the two compose per user.
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  super_admin: {
    can_access_analysis: true,
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: true,
    can_access_team_management: true,
    can_view_all_profiles: true,
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: true,
    can_manage_organization_members: true,
    can_set_organization_permissions: true,
  },
};

/**
 * Resolve the permissions a user actually holds, composing the scalar `role`
 * with the server-minted `org_admin[]` claim (HIERARCHY_SPEC §2).
 *
 * The rungs are not mutually exclusive: school-scoped power lives in the claim
 * array, so a `district_admin` who also runs a school holds both and the two
 * union here. Correspondingly, no entry in ROLE_PERMISSIONS grants a
 * school-scoped capability off the role string — `school_admin` is a
 * self-assignable profile label, and the claim is the only real grant.
 *
 * `viewingAs` is the super_admin "view as" override: the preview must show the
 * previewed role alone, not that role plus the previewer's own memberships.
 */
export const composeRolePermissions = (
  role: UserRole,
  orgAdminIds: string[] = [],
  viewingAs = false
): RolePermissions => {
  // Unrecognized roles fall back to school_admin, then athlete.
  const base = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.school_admin || ROLE_PERMISSIONS.athlete;
  if (viewingAs || orgAdminIds.length === 0) return base;
  return {
    ...base,
    can_manage_organization_members: true,
    can_set_organization_permissions: true,
  };
};

// Helper function to compute effective permissions for a user
export const computeUserPermissions = (
  userProfile: UserProfile,
  organizationPermissions: OrganizationPermission[] = []
): UserPermissions => {
  const base_permissions = ROLE_PERMISSIONS[userProfile.role];
  const enabled_org_permissions = userProfile.enabled_permissions || [];

  // Intersect organization permissions with user's enabled permissions
  const active_org_permissions = organizationPermissions.filter(
    permission => enabled_org_permissions.includes(permission)
  );

  return {
    base_permissions,
    organization_permissions: active_org_permissions,
    effective_permissions: {
      // Organization permissions are ADDITIVE to base permissions
      can_access_ai_analysis: base_permissions.can_access_analysis || active_org_permissions.includes('ai_analysis'),
      can_access_advanced_analytics: active_org_permissions.includes('advanced_analytics'),
      // Base permissions OR org permissions (additive)
      can_access_analysis: base_permissions.can_access_analysis || active_org_permissions.includes('ai_analysis'),
      can_access_challenges: base_permissions.can_access_challenges,
      can_access_feed: base_permissions.can_access_feed,
      can_access_admin: base_permissions.can_access_admin,
      can_access_team_management: base_permissions.can_access_team_management || active_org_permissions.includes('team_management'),
      can_view_all_profiles: base_permissions.can_view_all_profiles,
      can_manage_team: base_permissions.can_manage_team || active_org_permissions.includes('team_management'),
      // V1: Challenge creation is role-gated (coach/admin/super_admin), not org-permission granted
      can_create_challenges: base_permissions.can_create_challenges,
      can_manage_organizations: base_permissions.can_manage_organizations,
      can_manage_organization_members: base_permissions.can_manage_organization_members,
      can_set_organization_permissions: base_permissions.can_set_organization_permissions,
    }
  };
};

// Default organization permissions for different plan types
export const DEFAULT_ORGANIZATION_PERMISSIONS: Record<string, OrganizationPermission[]> = {
  basic: [], // Basic plan does not include AI analysis
  premium: ['ai_analysis'], // Premium plan includes AI analysis
};

// V1: Connection system removed - cross-org social features disabled for school safety

// ============================================================================
// POST REACTIONS (Replacing likes/comments)
// ============================================================================

export interface PostReaction {
  id: string;
  post_id: string;
  user_id: string;
  user_name?: string;
  user_photo_url?: string;
  emoji: ReactionEmoji;
  created_at: Date;
  updated_at: Date;
}

export interface ReactionCounts {
  '👍': number;
  '❤️': number;
  '👏': number;
  '🔥': number;
  '🎉': number;
}

// ============================================================================
// ANNOUNCEMENT CHANNELS
// ============================================================================

/**
 * Announcement channel, school- or district-scoped (HIERARCHY_SPEC §6.3).
 *
 * A school channel carries `organization_id`; a district channel carries
 * `district_id` and the schools it targets. Both target the same audiences —
 * a district channel simply resolves its audience across several schools.
 */
export interface AnnouncementChannel {
  id: string;
  name: string;
  description?: string;

  // Scope
  scope_type: 'organization' | 'district';
  /** School channels only. */
  organization_id?: string;
  /** District channels only; must equal the creator's district_id claim. */
  district_id?: string;
  /**
   * District channels only: the schools this channel reaches. null (or
   * absent) means every school in the district's `organization_ids`. The
   * fan-out intersects it with that list, so a stale or forged id reaches
   * no one.
   */
  target_organization_ids?: string[] | null;

  // Targeting
  target_audience: AnnouncementTargetAudience;

  // Creator
  created_by: string;
  created_by_type: 'school_admin' | 'district_admin';

  // Metadata
  created_at: Date;
  updated_at: Date;

  // Stats
  subscriber_count: number;
  message_count: number;
  last_message_at?: Date;
}

export interface AnnouncementMessage {
  id: string;
  channel_id: string;
  sender_id: string;
  sender_name: string;
  sender_photo_url?: string;
  content: string;
  created_at: Date;
  read_count: number;
  read_by?: string[]; // User IDs who have seen this announcement
  attachments?: PostAttachment[]; // Downloadable files
  // Machine translation — server-written only, forgery-guarded in rules
  // (docs/TRANSLATION_SPEC.md §4.5, §8)
  detected_lang?: string;
  translations?: ContentTranslations;
}

// ============================================================================
// PRIVACY HELPER TYPES (V1 Simplified)
// ============================================================================

export interface PrivacyCheckResult {
  canView: boolean;
  canMessage: boolean;
  canReact: boolean;
  reason?: string;
}

export interface UserPrivacySettings {
  profile_visibility: ProfileVisibility;
}
